import { useState, useEffect } from 'react'
import { BookOpen, Clock, CheckCircle, AlertCircle, GraduationCap } from 'lucide-react'
import { courses as courseService } from '../../services/apiService'
import { Loader } from '../UI/LoadingSpinner'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

const TABS = [
  { id: 'in-progress', label: 'In Progress',  icon: Clock,         color: 'yellow',  countKey: 'inProgressCoursesCount',  listKey: 'inProgressCourses' },
  { id: 'completed',   label: 'Completed',    icon: CheckCircle,   color: 'green',   countKey: 'completedCoursesCount',   listKey: 'completedCourses' },
  { id: 'not-started', label: 'Not Started',  icon: AlertCircle,   color: 'gray',    countKey: 'notStartedCoursesCount',  listKey: 'notStartedCourses' },
  { id: 'lessons',     label: 'Lessons',      icon: GraduationCap, color: 'purple',  countKey: 'completedLessonsCount',   listKey: null },
]

const colorMap = {
  yellow: { tab: 'text-yellow-600 dark:text-yellow-400 border-yellow-500', badge: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300', card: 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20' },
  green:  { tab: 'text-green-600 dark:text-green-400 border-green-500',   badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',   card: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' },
  gray:   { tab: 'text-light-text-secondary dark:text-dark-text-secondary border-light-text-secondary', badge: 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary', card: 'border-light-border-primary dark:border-dark-border-primary bg-light-bg-tertiary dark:bg-dark-bg-tertiary' },
  purple: { tab: 'text-purple-600 dark:text-purple-400 border-purple-500', badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300', card: 'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20' },
}

const CoursesSection = ({ authUserId, getToken }) => {
  const [courses, setCourses] = useState({
    courses: { enrolledCourses: [], completedCourses: [], inProgressCourses: [], notStartedCourses: [] },
    counts: { enrolledCoursesCount: 0, completedCoursesCount: 0, inProgressCoursesCount: 0, notStartedCoursesCount: 0, completedLessonsCount: 0 },
    lessons: { completedLessons: [] }
  })
  const [coursesLoading, setCoursesLoading] = useState(!!authUserId)
  const [coursesError, setCoursesError] = useState(null)
  const [activeTab, setActiveTab] = useState('in-progress')
  const [showAll, setShowAll] = useState(false)

  const PREVIEW_COUNT = 5

  useEffect(() => {
    if (authUserId) fetchCourses()
  }, [authUserId]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCourses = async () => {
    if (!authUserId) return
    const token = getToken()
    if (!token) {
      setCoursesError('Authorization required to view courses')
      setCoursesLoading(false)
      return
    }
    try {
      setCoursesLoading(true)
      setCoursesError(null)
      const result = await courseService.getUserAllPlatform(authUserId, token, 'courses')
      if (result.success && result.platforms?.courses) {
        setCourses({ ...result.platforms.courses })
      } else {
        setCoursesError(result.error || 'Error loading courses')
      }
    } catch (err) {
      console.error('Error fetching courses:', err)
      setCoursesError(err.message || 'Error loading courses')
    } finally {
      setCoursesLoading(false)
    }
  }

  const renderCourseCard = (course, index, color) => (
    <div key={index} className={`border rounded-lg p-4 ${colorMap[color].card}`}>
      <h5 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary mb-1">
        {course.title || course.name || 'Untitled'}
      </h5>
      {course.description && (
        <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mb-2">{course.description}</p>
      )}
      <div className="flex flex-wrap gap-3 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
        {course.institution && <span>Institution: {course.institution}</span>}
        {course.start_date && <span>Start: {new Date(course.start_date).toLocaleDateString('en-US')}</span>}
        {course.end_date && <span>End: {new Date(course.end_date).toLocaleDateString('en-US')}</span>}
        {course.duration && <span>Duration: {course.duration}</span>}
        {course.score && <span>Score: {course.score}</span>}
        {course.certificate_url && (
          <a href={course.certificate_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
            Certificate
          </a>
        )}
      </div>
    </div>
  )

  const renderTabContent = () => {
    if (activeTab === 'lessons') {
      const list = courses.lessons?.completedLessons || []
      if (list.length === 0) return <EmptyTab label="No completed lessons" />
      const visible = showAll ? list : list.slice(0, PREVIEW_COUNT)
      return (
        <>
          <div className="space-y-2">
            {visible.map((lesson, index) => (
              <div key={index} className={`border rounded-lg p-3 ${colorMap.purple.card}`}>
                <h5 className="font-medium text-xs text-light-text-primary dark:text-dark-text-primary">
                  {lesson.title || lesson.name || 'Untitled'}
                </h5>
                <div className="flex flex-wrap gap-3 text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
                  {lesson.course_title && <span>Course: {lesson.course_title}</span>}
                  {lesson.completed_at && <span>Completed: {new Date(lesson.completed_at).toLocaleDateString('en-US')}</span>}
                  {lesson.score && <span>Score: {lesson.score}</span>}
                </div>
              </div>
            ))}
          </div>
          <ShowAllButton total={list.length} shown={visible.length} showAll={showAll} onToggle={() => setShowAll(v => !v)} />
        </>
      )
    }

    const tab = TABS.find(t => t.id === activeTab)
    const list = courses.courses?.[tab.listKey] || []
    if (list.length === 0) return <EmptyTab label={`No ${tab.label.toLowerCase()} courses`} />
    const visible = showAll ? list : list.slice(0, PREVIEW_COUNT)
    return (
      <>
        <div className="space-y-3">{visible.map((c, i) => renderCourseCard(c, i, tab.color))}</div>
        <ShowAllButton total={list.length} shown={visible.length} showAll={showAll} onToggle={() => setShowAll(v => !v)} />
      </>
    )
  }

  return (
    <SectionBlock id="courses">
      <SectionHeader icon={BookOpen} title="Courses" />

      {coursesLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader variant="component" size="md" fullHeight={false} />
        </div>
      ) : !authUserId ? (
        <div className="text-center py-10">
          <BookOpen className="mx-auto h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mb-3" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">User not assigned</p>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Assign a user to this talent to view courses</p>
        </div>
      ) : coursesError ? (
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-10 w-10 text-red-400 dark:text-red-500 mb-3" />
          <p className="text-sm text-red-500 dark:text-red-400 mb-3">{coursesError}</p>
          <button onClick={() => { setCoursesError(null); fetchCourses() }} className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
            Try again
          </button>
        </div>
      ) : courses?.counts ? (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard value={courses.counts.enrolledCoursesCount} label="Enrolled" color="blue" />
            <StatCard value={courses.counts.completedCoursesCount} label="Completed" color="green" />
            <StatCard value={courses.counts.inProgressCoursesCount} label="In Progress" color="yellow" />
            <StatCard value={courses.counts.notStartedCoursesCount} label="Not Started" color="gray" />
          </div>

          {/* Tabs */}
          <div className="border-b border-light-border-primary dark:border-dark-border-primary">
            <div className="flex gap-1 overflow-x-auto">
              {TABS.map(tab => {
                const count = tab.id === 'lessons'
                  ? (courses.lessons?.completedLessons?.length || 0)
                  : (courses.courses?.[tab.listKey]?.length || 0)
                const isActive = activeTab === tab.id
                const TabIcon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setShowAll(false) }}
                    className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                      isActive
                        ? `${colorMap[tab.color].tab} border-b-2`
                        : 'text-light-text-tertiary dark:text-dark-text-tertiary border-transparent hover:text-light-text-primary dark:hover:text-dark-text-primary'
                    }`}
                  >
                    <TabIcon className="h-3.5 w-3.5" />
                    {tab.label}
                    {count > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${isActive ? colorMap[tab.color].badge : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-tertiary dark:text-dark-text-tertiary'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Tab content */}
          <div>{renderTabContent()}</div>
        </div>
      ) : (
        <div className="text-center py-8">
          <BookOpen className="mx-auto h-10 w-10 text-light-text-tertiary dark:text-dark-text-tertiary mb-3" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No courses found</p>
        </div>
      )}
    </SectionBlock>
  )
}

const StatCard = ({ value, label, color }) => {
  const styles = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400',
    green:  'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-600 dark:text-yellow-400',
    gray:   'bg-light-bg-tertiary dark:bg-dark-bg-tertiary border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary',
  }
  return (
    <div className={`text-center p-3 rounded-lg border ${styles[color]}`}>
      <div className="text-xl font-bold">{value || 0}</div>
      <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">{label}</div>
    </div>
  )
}

const ShowAllButton = ({ total, shown, showAll, onToggle }) => {
  if (total <= shown && !showAll) return null
  if (total <= 5) return null
  return (
    <button
      onClick={onToggle}
      className="mt-3 w-full py-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border border-dashed border-light-border-primary dark:border-dark-border-primary hover:border-primary-400 dark:hover:border-primary-500 rounded-lg transition-colors"
    >
      {showAll ? `Show less` : `Show all ${total} items`}
    </button>
  )
}

const EmptyTab = ({ label }) => (
  <div className="text-center py-8">
    <BookOpen className="mx-auto h-8 w-8 text-light-text-tertiary dark:text-dark-text-tertiary mb-2" />
    <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">{label}</p>
  </div>
)

export default CoursesSection
